import { has, isArray, isFunction, isObject, isString } from 'lodash'
import { computed, reactive } from 'vue'

import { checkSomeValueIsTruthy } from '@/utils/general'

/**
 * useFlagReactive
 *
 * Provides a reactive record for key-value objects, allowing you to track and update boolean states for each key.
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.object - An object whose values are used as keys in the record (must be primitive values for keys)
 * @param {boolean} [options.defaultValue=false] - The default value assigned to each key
 *
 * @returns {Object} An object containing:
 *   - flagValues: Computed array of current values for all keys
 *   - isAnyFlagEnabled: Computed boolean indicating if any key has a true value
 *   - setFlagValue: Function to set a value for a specific key
 *   - runWithFlag: Async function to set one or more keys to true, run a callback, then reset them to false
 *
 * @example
 * const { flagValues, setFlagValue, runWithFlag } = useFlagReactive({ object: { A: 'A', B: 'B' }, defaultValue: false })
 * setFlagValue('A', true)
 * await runWithFlag('A', () => { doSomethingWithFlag() }) // callback logic here
 * await runWithFlag(['A', 'B'], () => { doSomethingWithFlags() }) // callback logic here
 */
export const useFlagReactive = (options = { object: {}, defaultValue: false }) => {
  if (!isObject(options)) {
    throw new Error('useFlagReactive: options must be an object')
  }

  const defaultValue = Boolean(options.defaultValue)
  let cleanFlagObject = {}
  if (isObject(options.object) && !isArray(options.object)) {
    Object.values(options.object).forEach(value => {
      if (!isObject(value)) {
        cleanFlagObject[value] = defaultValue
      } else {
        throw new Error("useFlagReactive: object's values must be primitive values")
      }
    })
  }
  const flagObject = reactive(cleanFlagObject)

  const setFlagValue = (key, value) => {
    if (has(flagObject, key)) {
      flagObject[key] = value
    } else {
      console.error('useFlagReactive: key does not exist in object')
    }
  }

  const runWithFlag = async (key, callback) => {
    const keys = isString(key) ? [key] : isArray(key) ? key : []
    try {
      keys.forEach(k => setFlagValue(k, true))
      if (isFunction(callback)) {
        await callback()
      }
    } finally {
      keys.forEach(k => setFlagValue(k, false))
    }
  }

  return {
    flagValues: computed(() => Object.values(flagObject)),
    isAnyFlagEnabled: computed(() => checkSomeValueIsTruthy(...Object.values(flagObject))),
    setFlagValue,
    runWithFlag
  }
}
